import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { RichEditor, RichContent } from '../components/RichEditor';
import { VideoEmbed } from '../components/VideoEmbed';
import {
  MessageCircle, MoreHorizontal, Send, Loader2, Trash2,
  ExternalLink, BarChart3, Plus, X, Lightbulb,
  ChevronDown, ChevronUp, Flag, ShieldOff, BadgeCheck,
  Link2, Download, Eye,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMOJI_OPTIONS = ['\u2764\uFE0F', '\uD83D\uDC40', '\u2B06\uFE0F', '\u2B07\uFE0F', '\u2696\uFE0F'];
const EMOJI_LABELS = { '\u2764\uFE0F': 'Love', '\uD83D\uDC40': 'Watching', '\u2B06\uFE0F': 'Upvote', '\u2B07\uFE0F': 'Downvote', '\u2696\uFE0F': 'Legal' };

const PROMPT_SETS = {
  news: ["How might this affect your case?", "Has anyone experienced something similar?", "What should people prepare for?", "Is this consistent with your lawyer's advice?", "Could this create new opportunities?"],
  poll: ["Why did you pick that option?", "What factors influenced your choice?", "Is there an option you wish was listed?"],
  legal: ["Have you consulted a professional?", "What documentation helped?", "Any timelines to be aware of?", "What would you tell someone starting this?"],
  general: ["What's your perspective?", "How does this connect to your journey?", "Any insights from your experience?", "What would you add?", "Anything others should keep in mind?"],
};

const LEGAL_KEYWORDS = ['visa', 'green card', 'h-1b', 'immigration', 'uscis', 'petition', 'asylum', 'lawyer', 'attorney', 'legal', 'court', 'filing', 'citizenship'];

function getSmartPrompt(post) {
  let pool = PROMPT_SETS.general;
  if (post.is_news) pool = PROMPT_SETS.news;
  else if (post.poll) pool = PROMPT_SETS.poll;
  else if (LEGAL_KEYWORDS.some(kw => (post.content || '').toLowerCase().includes(kw))) pool = PROMPT_SETS.legal;
  const idx = post.post_id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % pool.length;
  return pool[idx];
}

// Extract URL from text
function extractUrl(text) {
  if (!text) return null;
  const m = text.match(/(https?:\/\/[^\s\])<]+)/);
  return m ? m[1] : null;
}

function isYouTubeUrl(url) {
  if (!url) return false;
  return /(?:youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+/.test(url);
}

const DashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [mentions, setMentions] = useState([]);
  const [urlPreview, setUrlPreview] = useState(null);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [userReactions, setUserReactions] = useState({});
  const previewTimeoutRef = useRef(null);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/posts/`, { headers: getAuthHeaders(), withCredentials: true });
      setPosts(response.data);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // URL preview detection on post content change
  useEffect(() => {
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    const url = extractUrl(newPost);
    if (url && !isYouTubeUrl(url)) {
      previewTimeoutRef.current = setTimeout(async () => {
        setFetchingPreview(true);
        try {
          const res = await axios.get(`${API}/posts/url-preview`, {
            params: { url }, headers: getAuthHeaders(), withCredentials: true
          });
          if (res.data.title) setUrlPreview(res.data);
          else setUrlPreview(null);
        } catch { setUrlPreview(null); }
        finally { setFetchingPreview(false); }
      }, 800);
    } else {
      setUrlPreview(null);
    }
    return () => { if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current); };
  }, [newPost, getAuthHeaders]);

  const handleCreatePost = async () => {
    if (!newPost.trim() && !pollQuestion.trim()) return;
    setPosting(true);
    try {
      const payload = { content: newPost || (pollQuestion ? `Poll: ${pollQuestion}` : ''), is_anonymous: isAnonymous, format: 'rich', mentions };
      // Auto-detect YouTube URL and set video_url
      const detectedUrl = extractUrl(newPost);
      if (detectedUrl && isYouTubeUrl(detectedUrl)) {
        payload.video_url = detectedUrl;
      }
      const videoMatch = newPost.match(/\[video:(.*?)\]/);
      if (videoMatch) payload.video_url = videoMatch[1];
      if (showPollCreator && pollQuestion.trim()) {
        const validOptions = pollOptions.filter(o => o.trim());
        if (validOptions.length < 2) { toast.error('Poll needs at least 2 options'); setPosting(false); return; }
        payload.poll = { question: pollQuestion, options: validOptions };
      }
      await axios.post(`${API}/posts/`, payload, { headers: getAuthHeaders(), withCredentials: true });
      setNewPost(''); setIsAnonymous(false); setShowPollCreator(false); setPollQuestion(''); setPollOptions(['', '']); setMentions([]); setUrlPreview(null);
      toast.success('Post created!');
      fetchPosts();
    } catch { toast.error('Failed to create post'); }
    finally { setPosting(false); }
  };

  const handleReaction = async (postId, emoji) => {
    try {
      const res = await axios.post(`${API}/posts/${postId}/react`, { emoji }, { headers: getAuthHeaders(), withCredentials: true });
      // Optimistic update
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, reactions: res.data.reactions } : p));
      setUserReactions(prev => ({ ...prev, [postId]: res.data.user_reaction }));
    } catch { toast.error('Failed to react'); }
  };

  const handleVote = async (postId, optionId) => {
    try {
      const res = await axios.post(`${API}/posts/${postId}/vote`, { option_id: optionId }, { headers: getAuthHeaders(), withCredentials: true });
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, poll: res.data.poll } : p));
    } catch { toast.error('Failed to vote'); }
  };

  const handleDeletePost = async (postId) => {
    try { await axios.delete(`${API}/posts/${postId}`, { headers: getAuthHeaders(), withCredentials: true }); toast.success('Post deleted'); fetchPosts(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleReport = async (targetId, targetType) => {
    const reason = prompt('Reason for reporting:');
    if (!reason) return;
    try { await axios.post(`${API}/reports/`, { target_type: targetType, target_id: targetId, reason }, { headers: getAuthHeaders(), withCredentials: true }); toast.success('Report submitted'); }
    catch { toast.error('Failed to report'); }
  };

  const handleBlock = async (userId) => {
    try { await axios.post(`${API}/users/${userId}/block`, {}, { headers: getAuthHeaders(), withCredentials: true }); toast.success('User blocked'); }
    catch { toast.error('Failed to block'); }
  };

  const fetchComments = async (postId) => {
    try { const r = await axios.get(`${API}/posts/${postId}/comments`, { headers: getAuthHeaders(), withCredentials: true }); setComments(prev => ({ ...prev, [postId]: r.data })); }
    catch { toast.error('Failed to load comments'); }
  };

  const toggleComments = (postId) => {
    if (!expandedComments[postId]) fetchComments(postId);
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleAddComment = async (postId) => {
    const content = newComments[postId]?.trim();
    if (!content) return;
    try { await axios.post(`${API}/posts/${postId}/comment`, { content }, { headers: getAuthHeaders(), withCredentials: true }); setNewComments(prev => ({ ...prev, [postId]: '' })); fetchComments(postId); fetchPosts(); }
    catch { toast.error('Failed to add comment'); }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const diff = Date.now() - date;
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const hasUserVoted = (poll) => poll?.voters?.includes(user?.user_id);

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-3 sm:px-4 pt-20 sm:pt-24 pb-8 sm:pb-12">
        {/* Create Post */}
        <Card className="mb-4 sm:mb-6 card-default" data-testid="create-post-card">
          <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
            <div className="flex gap-3 sm:gap-4">
              <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-border flex-shrink-0">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm">{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-3 sm:space-y-4 min-w-0">
                <RichEditor placeholder="Share your thoughts..." value={newPost} onChange={setNewPost} onMentionsChange={setMentions} className="input-default text-sm sm:text-base" data-testid="post-content-input" />
                
                {/* URL Preview */}
                {fetchingPreview && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Fetching link preview...</div>
                )}
                {urlPreview && (
                  <div className="rounded-lg border border-border/60 bg-muted/30 overflow-hidden" data-testid="url-preview">
                    <div className="flex items-start gap-3 p-3">
                      {urlPreview.image && (
                        <img src={urlPreview.image} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" onError={e => e.target.style.display = 'none'} />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{urlPreview.title}</p>
                        {urlPreview.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{urlPreview.description}</p>}
                        <div className="flex items-center gap-1 mt-1">
                          <Link2 className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground truncate">{urlPreview.site_name || new URL(urlPreview.url).hostname}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => setUrlPreview(null)}><X className="h-3 w-3" /></Button>
                    </div>
                  </div>
                )}

                {showPollCreator && (
                  <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 space-y-3" data-testid="poll-creator">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-primary"><BarChart3 className="h-4 w-4" />Create Poll</div>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowPollCreator(false)}><X className="h-3 w-3" /></Button>
                    </div>
                    <Input placeholder="Ask a question..." value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} className="input-default text-sm" data-testid="poll-question-input" />
                    {pollOptions.map((opt, i) => (
                      <div key={i} className="flex gap-2">
                        <Input placeholder={`Option ${i + 1}`} value={opt} onChange={e => { const u = [...pollOptions]; u[i] = e.target.value; setPollOptions(u); }} className="input-default text-sm flex-1" data-testid={`poll-option-input-${i}`} />
                        {pollOptions.length > 2 && <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}><X className="h-3 w-3" /></Button>}
                      </div>
                    ))}
                    {pollOptions.length < 6 && <Button variant="ghost" size="sm" className="text-primary text-xs" onClick={() => setPollOptions([...pollOptions, ''])} data-testid="add-poll-option-btn"><Plus className="h-3 w-3 mr-1" />Add option</Button>}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch id="anonymous" checked={isAnonymous} onCheckedChange={setIsAnonymous} data-testid="anonymous-toggle" />
                      <Label htmlFor="anonymous" className="text-xs sm:text-sm text-muted-foreground">Anonymous</Label>
                    </div>
                    {!showPollCreator && <Button variant="ghost" size="sm" className="text-muted-foreground h-8 px-2" onClick={() => setShowPollCreator(true)} data-testid="toggle-poll-btn"><BarChart3 className="h-4 w-4 mr-1" /><span className="text-xs">Poll</span></Button>}
                  </div>
                  <Button onClick={handleCreatePost} disabled={(!newPost.trim() && !pollQuestion.trim()) || posting} className="btn-primary w-full sm:w-auto" data-testid="submit-post-btn">
                    {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />Post</>}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feed */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : posts.length === 0 ? (
          <Card className="card-default text-center py-12"><p className="text-muted-foreground">No posts yet. Be the first to share!</p></Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {posts.map((post, index) => (
              <PostCard key={post.post_id} post={post} index={index} user={user}
                onReaction={handleReaction} onVote={handleVote} onDelete={handleDeletePost}
                onReport={handleReport} onBlock={handleBlock} onToggleComments={toggleComments}
                expandedComments={expandedComments} comments={comments}
                newComments={newComments} setNewComments={setNewComments}
                onAddComment={handleAddComment} formatDate={formatDate} hasUserVoted={hasUserVoted}
                userReactions={userReactions} getAuthHeaders={getAuthHeaders}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LinkPreviewCard = ({ content }) => {
  const [preview, setPreview] = useState(null);
  const { getAuthHeaders } = useAuth();
  
  useEffect(() => {
    const url = extractUrl(content);
    if (!url || isYouTubeUrl(url)) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/posts/url-preview`, { params: { url }, headers: getAuthHeaders(), withCredentials: true });
        if (res.data.title) setPreview(res.data);
      } catch {}
    };
    load();
  }, [content, getAuthHeaders]);

  if (!preview) return null;
  return (
    <a href={preview.url} target="_blank" rel="noopener noreferrer" className="block rounded-lg border border-border/60 bg-muted/30 overflow-hidden hover:bg-muted/50 transition-colors" data-testid="link-preview-card">
      {preview.image && <img src={preview.image} alt="" className="w-full h-32 sm:h-40 object-cover" onError={e => e.target.style.display = 'none'} />}
      <div className="p-3">
        <p className="text-sm font-medium line-clamp-2">{preview.title}</p>
        {preview.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{preview.description}</p>}
        <div className="flex items-center gap-1 mt-1.5">
          <ExternalLink className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{preview.site_name || new URL(preview.url).hostname}</span>
        </div>
      </div>
    </a>
  );
};

const PostCard = ({ post, index, user, onReaction, onVote, onDelete, onReport, onBlock, onToggleComments, expandedComments, comments, newComments, setNewComments, onAddComment, formatDate, hasUserVoted, userReactions, getAuthHeaders }) => {
  const commentPrompt = getSmartPrompt(post);
  const detectedUrl = extractUrl(post.content);
  const hasYouTube = detectedUrl && isYouTubeUrl(detectedUrl);
  
  return (
    <Card className="card-default overflow-hidden" data-testid={`post-${post.post_id}`}>
      <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-border flex-shrink-0">
              <AvatarImage src={post.is_anonymous ? null : post.author_picture} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs sm:text-sm">{post.author_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <p className="font-medium text-sm sm:text-base truncate">{post.is_anonymous ? 'Anonymous' : post.author_name}</p>
                {post.author_verified && !post.is_anonymous && <BadgeCheck className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
              </div>
              <p className="text-xs text-muted-foreground">{formatDate(post.created_at)}</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(post.author_id === user?.user_id || user?.role === 'admin') && (
                <DropdownMenuItem onClick={() => onDelete(post.post_id)} className="text-destructive" data-testid={`delete-post-${post.post_id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
              )}
              {post.author_id !== user?.user_id && (
                <>
                  <DropdownMenuItem onClick={() => onReport(post.post_id, 'post')} data-testid={`report-post-${post.post_id}`}><Flag className="h-4 w-4 mr-2" />Report</DropdownMenuItem>
                  {!post.is_anonymous && <DropdownMenuItem onClick={() => onBlock(post.author_id)} data-testid={`block-user-${post.author_id}`}><ShieldOff className="h-4 w-4 mr-2" />Block User</DropdownMenuItem>}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 px-3 sm:px-6 pb-3 sm:pb-4">
        {post.is_news ? (
          <div className="space-y-3">
            {post.news_image && <div className="rounded-lg overflow-hidden bg-muted -mx-3 sm:mx-0"><div className="aspect-video sm:aspect-[2/1]"><img src={post.news_image} alt={post.news_title} className="w-full h-full object-cover" onError={e => e.target.style.display = 'none'} /></div></div>}
            <h3 className="font-serif text-base sm:text-lg font-semibold leading-tight">{post.news_title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{post.content}</p>
            <a href={post.news_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 transition-colors" data-testid={`news-link-${post.post_id}`}>
              <span className="text-xs sm:text-sm font-medium text-primary truncate max-w-[150px] sm:max-w-none">{post.news_domain || post.news_source}</span>
              <ExternalLink className="h-3 w-3 text-primary flex-shrink-0" />
            </a>
          </div>
        ) : (
          <>
            <RichContent content={post.content} format={post.format} />
            {/* YouTube/Video embed - only if not already embedded via [video:] tag in content */}
            {(() => {
              const hasVideoTag = (post.content || '').includes('[video:');
              if (hasVideoTag) return null; // RichContent already handles it
              if (hasYouTube) return <VideoEmbed url={detectedUrl} />;
              if (post.video_url) return <VideoEmbed url={post.video_url} />;
              return null;
            })()}
            {/* Link preview for non-YouTube, non-video URLs */}
            {!post.is_news && !hasYouTube && !post.video_url && !(post.content || '').includes('[video:') && <LinkPreviewCard content={post.content} />}
          </>
        )}
        {post.poll && <PollDisplay post={post} onVote={onVote} user={user} hasUserVoted={hasUserVoted} getAuthHeaders={getAuthHeaders} />}
        <div className="flex items-center gap-1">
          {EMOJI_OPTIONS.map(emoji => {
            const count = post.reactions?.[emoji] || 0;
            const isMyReaction = userReactions?.[post.post_id] === emoji;
            return (
              <button key={emoji} onClick={() => onReaction(post.post_id, emoji)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all hover:scale-105 ${isMyReaction ? 'bg-primary/20 border-2 border-primary/40 text-primary ring-1 ring-primary/20' : count > 0 ? 'bg-primary/10 border border-primary/20 text-primary' : 'bg-muted/50 hover:bg-muted text-muted-foreground'}`} title={`${EMOJI_LABELS[emoji]}${isMyReaction ? ' (your reaction)' : ''}`} data-testid={`react-${emoji}-${post.post_id}`}>
                <span className="text-sm">{emoji}</span>{count > 0 && <span className="font-medium">{count}</span>}
              </button>
            );
          })}
        </div>
        <Separator className="my-1" />
        <div className="flex items-center">
          <Button variant="ghost" size="sm" className="text-muted-foreground h-8 px-2 sm:px-3" onClick={() => onToggleComments(post.post_id)} data-testid={`comment-btn-${post.post_id}`}>
            {expandedComments[post.post_id] ? <ChevronUp className="h-4 w-4 sm:mr-1" /> : <ChevronDown className="h-4 w-4 sm:mr-1" />}
            <MessageCircle className="h-4 w-4 sm:mr-1 ml-1" /><span className="ml-1">{post.comment_count || 0}</span>
          </Button>
        </div>
        {expandedComments[post.post_id] && (
          <div className="space-y-3 pt-2" data-testid={`comments-section-${post.post_id}`}>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/15">
              <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-600 dark:text-amber-400 italic">{commentPrompt}</p>
            </div>
            {comments[post.post_id]?.map(comment => (
              <div key={comment.comment_id} className="flex gap-2 sm:gap-3" data-testid={`comment-${comment.comment_id}`}>
                <Avatar className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0 mt-1"><AvatarImage src={comment.author_picture} /><AvatarFallback className="text-[10px] bg-muted">{comment.author_name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><div className="relative bg-muted/60 rounded-2xl rounded-tl-sm px-3 py-2 border border-border/50"><p className="text-xs font-medium text-primary/80 mb-0.5">{comment.author_name}</p><p className="text-xs sm:text-sm break-words leading-relaxed">{comment.content}</p></div></div>
              </div>
            ))}
            <div className="flex gap-2 sm:gap-3 pt-1">
              <Avatar className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0 mt-1"><AvatarImage src={user?.picture} /><AvatarFallback className="text-[10px] bg-muted">{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
              <div className="flex-1 flex gap-2 min-w-0">
                <Textarea placeholder={commentPrompt} value={newComments[post.post_id] || ''} onChange={e => setNewComments(prev => ({ ...prev, [post.post_id]: e.target.value }))} className="min-h-[36px] sm:min-h-[40px] resize-none input-default text-sm rounded-2xl" data-testid={`comment-input-${post.post_id}`} />
                <Button size="icon" onClick={() => onAddComment(post.post_id)} disabled={!newComments[post.post_id]?.trim()} className="btn-primary h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 rounded-full" data-testid={`submit-comment-${post.post_id}`}><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PollDisplay = ({ post, onVote, user, hasUserVoted, getAuthHeaders }) => {
  const poll = post.poll;
  const voted = hasUserVoted(poll);
  const total = poll.total_votes || 0;
  const isAdmin = user?.role === 'admin';
  const isAuthor = post.author_id === user?.user_id;
  const canManage = isAdmin || isAuthor;
  const [downloading, setDownloading] = useState(false);

  // Get user's current vote
  const userVoteOptionId = poll.voter_data?.[user?.user_id]?.option_id;

  const handleDownloadResults = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/posts/${post.post_id}/poll-results`, { headers: getAuthHeaders(), withCredentials: true });
      const data = res.data;
      let csv = 'User,Option,Voted At\n';
      data.results.forEach(r => { csv += `"${r.user_name}","${r.option}","${r.voted_at}"\n`; });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `poll_results_${post.post_id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Results downloaded!');
    } catch { toast.error('Failed to download results'); }
    finally { setDownloading(false); }
  };

  const handlePublish = async () => {
    try {
      await axios.put(`${API}/posts/${post.post_id}/poll-publish`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Results published!');
    } catch { toast.error('Failed to publish'); }
  };

  return (
    <div className="p-3 rounded-lg border border-border/60 bg-muted/30 space-y-3" data-testid={`poll-${post.post_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">{poll.question}</p></div>
        {voted && <Badge variant="outline" className="text-xs">Tap to change vote</Badge>}
      </div>
      <div className="space-y-2">
        {poll.options.map(opt => {
          const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
          const isMyVote = userVoteOptionId === opt.option_id;
          return (
            <button key={opt.option_id} onClick={() => onVote(post.post_id, opt.option_id)} className={`w-full text-left rounded-lg px-3 py-2 relative overflow-hidden border transition-all ${isMyVote ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/20' : 'border-border/50 hover:border-primary/40 hover:bg-primary/5 cursor-pointer'}`} data-testid={`poll-option-${opt.option_id}`}>
              {voted && <div className="absolute inset-0 bg-primary/10 rounded-lg" style={{ width: `${pct}%` }} />}
              <div className="relative flex items-center justify-between">
                <span className="text-sm">{opt.text} {isMyVote && <span className="text-xs text-primary ml-1">(your vote)</span>}</span>
                {voted && <span className="text-xs font-medium text-muted-foreground ml-2">{pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{total} vote{total !== 1 ? 's' : ''}{poll.is_published && ' · Results published'}</p>
        {canManage && total > 0 && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleDownloadResults} disabled={downloading} data-testid={`download-poll-${post.post_id}`}>
              <Download className="h-3 w-3 mr-1" />{downloading ? 'Loading...' : 'CSV'}
            </Button>
            {!poll.is_published && (
              <Button variant="ghost" size="sm" className="text-xs h-7 text-primary" onClick={handlePublish} data-testid={`publish-poll-${post.post_id}`}>
                <Eye className="h-3 w-3 mr-1" />Publish
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
